import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "./App.css";

import Loader from "./components/Loader";
import LayoutView from "./modules/layout/LayoutView";
import Notification from "./modules/layout/Notification";
import lazyComponentLoader from "./hoc/LazyLoader";
import ProtectedRoute from "./hoc/ProtectedRoute";
import UnprotectedRoute from "./hoc/UnprotectedRoute";

import CreateOnBoardingAccount from "./modules/onboarding-account/CreateOnBoardingAccount";
import ReauthOnBoardingAccount from "./modules/onboarding-account/ReauthOnBoardingAccount";
import SuccessOnBoardingAccount from "./modules/onboarding-account/SuccessOnBoardingAccount";

// ─── Lazy Modules ─────────────────────────────────────────────────────────────
const Login = lazyComponentLoader(() => import("./modules/auth/login/Login"));
const Forgot = lazyComponentLoader(() => import("./modules/auth/forgot-password/ForgotPassword"));
const Reset = lazyComponentLoader(() => import("./modules/auth/forgot-password/ResetPassword"));
const Register = lazyComponentLoader(() => import("./modules/auth/register/RegisterView"));

const Dashboard = lazyComponentLoader(() => import("./modules/dashboard/DashboardView"));
const RestaurantDetail = lazyComponentLoader(() => import("./modules/restaurant-detail/RestaurantDetailManagementView"));
const MenuManagement = lazyComponentLoader(() => import("./modules/menu-management/MenuManagementView"));
const PickupManagement = lazyComponentLoader(() => import("./modules/pickup-management/PickupManagementView"));

const DineManagement = lazyComponentLoader(() => import("./modules/dinein-management/DineinManagementView"));
const DeliveryManagement = lazyComponentLoader(() => import("./modules/delivery-management/DeliveryManagementView"));


const OrderManagement = lazyComponentLoader(() => import("./modules/order-management/OrderManagementView"));
const OrderHistoryManagement = lazyComponentLoader(() => import("./modules/sales-history-management/SalesHistoryManagementView")); // DONE W/AI
const ReceivableManagement = lazyComponentLoader(() => import("./modules/receivable-management/ReceivableManagementView")); // DONE

const AboutUsView = lazyComponentLoader(() => import("./modules/about-us/AboutUsView")); // DONE
const ProfileManagement = lazyComponentLoader(() => import("./modules/profile/ProfileManagementView")); // DONE


// ─── App ──────────────────────────────────────────────────────────────────────
const App = () => {
  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('.ant-pagination .ant-select') &&
        !['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab'].includes(e.key)) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, []);

  useEffect(() => {
    const inc = () => setLoadingCount(c => c + 1);
    const dec = () => setLoadingCount(c => Math.max(0, c - 1));
    window.addEventListener('api:loading:start', inc);
    window.addEventListener('api:loading:end', dec);
    return () => {
      window.removeEventListener('api:loading:start', inc);
      window.removeEventListener('api:loading:end', dec);
    };
  }, []);

  return (
    <div>
      <Notification />
      <Routes>
        <Route element={<LayoutView />}>
          {/* ── Unprotected ── */}
          <Route path="/login" element={<UnprotectedRoute element={<Login />} />} />
          <Route path="/forgot-password" element={<UnprotectedRoute element={<Forgot />} />} />
          <Route path="/reset-password" element={<UnprotectedRoute element={<Reset />} />} />
          <Route path="/register" element={<UnprotectedRoute element={<Register />} />} />
          <Route path="/about" element={<UnprotectedRoute element={<AboutUsView />} />} />

          {/* ── Protected ── */}
          <Route index element={<ProtectedRoute element={<RestaurantDetail />} />} />
          <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
          <Route path="/restaurant_detail" element={<ProtectedRoute element={<RestaurantDetail />} />} />
          <Route path="/menu" element={<ProtectedRoute element={<MenuManagement />} />} />
          <Route path="/pickup" element={<ProtectedRoute element={<PickupManagement />} />} />
          <Route path="/reservations" element={<ProtectedRoute element={<DineManagement />} />} />
          <Route path="/delivery" element={<ProtectedRoute element={<DeliveryManagement />} />} />
          <Route path="/orders" element={<ProtectedRoute element={<OrderManagement />} />} />
          <Route path="/sales_history" element={<ProtectedRoute element={<OrderHistoryManagement />} />} />
          <Route path="/receivable" element={<ProtectedRoute element={<ReceivableManagement />} />} />
          <Route path="/profile" element={<ProtectedRoute element={<ProfileManagement />} />} />

          <Route path="/onboarding" element={<ProtectedRoute element={<CreateOnBoardingAccount />} />} />
          <Route path="/onboarding/reauth" element={<ProtectedRoute element={<ReauthOnBoardingAccount />} />} />
          <Route path="/onboarding/success" element={<ProtectedRoute element={<SuccessOnBoardingAccount />} />} />

          {/* ── Fallback ── */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>

      {isLoading && <Loader />}
    </div>
  );
};

export default App;

